import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { ArrowDownloadFilled, Dismiss24Regular } from '@fluentui/react-icons';
import { useEffect, useState } from 'react';
import { ButtonLoading, OptionSwatch } from '@shared/components';
import { TExportDialogState, TVersionPreference } from '@modules/items';
import { useBulkExport } from '../../api/bulkExports.api';
import { useBulkExportSubmission } from '@modules/submissions';

type TProps = {
  state?: TExportDialogState;
  setState: (state: TExportDialogState) => void;
};

const ExportDialog = ({ state, setState }: TProps) => {
  const [selectedMode, setSelectedMode] = useState<TVersionPreference>('DRAFT');

  useEffect(() => {
    if (state?.isOpen) setSelectedMode('DRAFT');
  }, [state?.isOpen]);

  const affectedItems = state?.affectedItems || [];

  const bulkExport = useBulkExport();
  const bulkExportSubmission = useBulkExportSubmission();
  const handleBulkExport = () => {
    if (!state) return;
    if (state.submissionId) {
      bulkExportSubmission.mutate({
        submissionId: state.submissionId,
        // check `state.affectedItems`, as `undefined` exports ALL submission items
        itemIds: state.affectedItems && affectedItems.map((i) => i._id),
        mode: selectedMode,
      });
    } else if (affectedItems.length > 0) {
      bulkExport.mutate({
        itemIds: affectedItems.map((item) => item._id),
        mode: selectedMode,
      });
    }

    setState({ ...state, isOpen: false });
  };

  return (
    <Dialog
      open={state?.isOpen || false}
      onOpenChange={(_, data) => {
        if (state) setState({ ...state, isOpen: data.open });
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            {state?.submissionId
              ? `Select fallback version for export`
              : `Export ${affectedItems.length === 1 ? affectedItems[0].name : affectedItems.length + ' items'}`}
          </DialogTitle>
          <DialogContent>
            {state?.message && (
              <MessageBar intent="warning" className="mb-2">
                <MessageBarBody>{state.message}</MessageBarBody>
              </MessageBar>
            )}
            <OptionSwatch
              label="Select version:"
              selectedValue={selectedMode}
              setSelectedValue={setSelectedMode as (val: string) => void}
              options={[
                {
                  value: 'DRAFT',
                  title: 'Draft / latest version',
                  tooltip: 'Export draft, or the latest version if no draft is open.',
                },
                {
                  value: 'VERSION',
                  title: (
                    <>
                      Latest
                      <br />
                      version
                    </>
                  ),
                  tooltip: 'Export the latest version.',
                },
                {
                  value: 'APPROVED',
                  title: 'Approved version',
                  tooltip: 'Only export the latest approved version.',
                },
              ]}
              className="mb-4"
            />
          </DialogContent>
          <DialogActions>
            <ButtonLoading
              appearance="primary"
              icon={<ArrowDownloadFilled />}
              onClick={handleBulkExport}
              loading={bulkExport.isPending || bulkExportSubmission.isPending}
            >
              Export
            </ButtonLoading>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ExportDialog;
